import { APP_IMAGES } from "../../../constants/images";
import { useCallback, useEffect } from "react";
import { APP_ROUTES, AUTH_ROUTES, ROUTE_NAMES } from "../../../routes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cn } from "../../../utils/cn";
import { useTranslation } from "react-i18next";
import { Toaster } from "react-hot-toast";
import useAuthStore from "../../../store/Auth";
import SwitchLanguageCard from "../../../components/card/SwitchLanguageCard";
import Loader from "../../../components/loader";
import { USER_ROLES_IDS } from "../../../constants/common";
import { RolePrivilege } from "../../../store/Auth/interface";
import LoginForm from "./LoginForm";
import { useTableStore } from "../../../store/Table";
import LoginWithSSOCard from "../../../components/card/LoginWithSSOCard";

const Login = () => {
  const [searchParams] = useSearchParams();
  const isSSOLogin = searchParams.get("isSSOLogin");
  const forceLogout = searchParams.get("forceLogout");

  const params = new URLSearchParams(window.location.hash.substring(1));
  const error = params.get("error");

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { isLoggedIn, user, userRoles, reset } = useAuthStore();
  const { resetTableStore } = useTableStore();

  useEffect(() => {
    if (forceLogout === "true") {
      sessionStorage.clear();
      reset();
      resetTableStore();

      navigate(`${AUTH_ROUTES.LOGIN}?isSSOLogin=true`);
    }
  }, [forceLogout, navigate, reset, resetTableStore]);

  const handleNavigateToDefaultPage = useCallback(
    (privileges: any, decodedUser?: { RoleId: number }) => {
      if (Object.keys(privileges).length > 0) {
        const userRole = decodedUser?.RoleId || user?.n_RoleID;

        const isHomePrivilege = privileges?.rolePrivileges?.some(
          (privilege: RolePrivilege) =>
            privilege.s_RouteName === ROUTE_NAMES.HOME_SEARCH,
        );

        const isApproveVisitRequestsPrivilege =
          privileges?.rolePrivileges?.some(
            (privilege: RolePrivilege) =>
              privilege.s_RouteName ===
              ROUTE_NAMES.APPROVE_VISIT_REQUEST_SEARCH,
          );

        const isApprovePermitRequestsPrivilege =
          privileges?.rolePrivileges?.some(
            (privilege: RolePrivilege) =>
              privilege.s_RouteName ===
              ROUTE_NAMES.APPROVE_PERMIT_REQUEST_SEARCH,
          );

        if (
          Number(userRole) === USER_ROLES_IDS.ADMIN ||
          Number(userRole) === USER_ROLES_IDS.SECURITY ||
          Number(userRole) === USER_ROLES_IDS.SECURITY_MANAGER ||
          Number(userRole) === USER_ROLES_IDS.RECEPTION
        ) {
          navigate(isHomePrivilege ? APP_ROUTES.HOME : APP_ROUTES.UNAUTHORIZED);
        } else if (
          Number(userRole) === USER_ROLES_IDS.DIRECT_MANAGER ||
          Number(userRole) === USER_ROLES_IDS.INVENTORY_MANAGER
        ) {
          navigate(
            isApprovePermitRequestsPrivilege
              ? APP_ROUTES.APPROVE_PERMIT_REQUEST
              : APP_ROUTES.UNAUTHORIZED,
          );
        } else {
          navigate(
            isApproveVisitRequestsPrivilege
              ? APP_ROUTES.APPROVE_VISIT_REQUEST
              : APP_ROUTES.UNAUTHORIZED,
          );
        }
      }
    },
    [navigate, user?.n_RoleID],
  );

  useEffect(() => {
    if (isLoggedIn) {
      handleNavigateToDefaultPage(userRoles);
    }
  }, [handleNavigateToDefaultPage, isLoggedIn, navigate, user, userRoles]);

  if (isLoggedIn) {
    return (
      <div className="flex justify-center mt-[3vh]">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <SwitchLanguageCard
        styles={cn(
          "absolute top-1 sm:top-2 max-w-max bg-white",
          i18n.language === "en" ? "right-2" : "left-2",
        )}
      />
      <div className="bg-purple-primary/10 dark:bg-purple-primary/40 dark:text-black py-[8vh] sm:py-0">
        <section className="flex flex-col md:flex-row justify-center items-center py-8 md:py-16 mx-6 md:mx-0 h-dvh">
          <div className="sm:min-w-[60vw] md:min-w-[430px] xl:w-1/4 flex flex-col px-5 py-3 gap-2 bg-white rounded-2xl">
            <div>
              <img
                className="rounded-none mx-auto w-32 lg:w-36 object-contain"
                src={APP_IMAGES.logo}
                alt=""
              />
            </div>
            <div>
              <h1 className="font-semibold text-lg sm:text-xl md:text-2xl text-center ">
                {t("welcomeTo")}
                <span className="font-bold text-[#413E6C]">
                  {" "}
                  {t("mofaShort")}
                </span>
              </h1>
              <h1 className="font-medium sm:text-lg md:text-xl text-center">
                {t("visitorManagementSystem")}
              </h1>
            </div>

            {isSSOLogin || error ? (
              <div className="flex flex-col gap-y-1">
                <LoginWithSSOCard error={error} />

                <div className="flex items-center justify-center gap-x-4">
                  <div className="h-[0.5px] w-full bg-black" />
                  <p className="text-sm">OR</p>
                  <div className="h-[0.5px] w-full bg-black" />
                </div>
              </div>
            ): null}

            <LoginForm
              handleNavigateToDefaultPage={handleNavigateToDefaultPage}
            />
          </div>
        </section>
      </div>
    </>
  );
};

export default Login;
